import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from 'src/environments/environment';

/** A staff member's ETH Pulse agent, as Pulse reports it. */
export interface PulseAgent {
  eth_user_id: string;
  agent_id: number;
  email: string;
  enabled: boolean;
  roles: string[];
  teams: string[];
}

/** What Role Assignment needs to show and manage Pulse access. */
export interface PulseStaffOverview {
  /** Pulse team names. */
  teams: string[];
  /** eth role -> Pulse role and default teams, configured in Pulse. */
  agent_roles: Record<string, { role: string; teams: string[] }>;
  /** eth user id -> agent, for staff who have Pulse access. */
  agents: Record<string, PulseAgent>;
  /** eth user id -> eth role, for staff who can be given access. */
  eligible: Record<string, string>;
}

/**
 * eth staff access to ETH Pulse: giving and removing access (Role Assignment), and "Open Pulse",
 * which signs the staff member in with a one-time link. eth is the only way in for staff.
 */
@Injectable({ providedIn: 'root' })
export class PulseStaffService {
  constructor(private http: HttpClient) {}

  overview(): Observable<PulseStaffOverview> {
    return this.http.get<PulseStaffOverview>(`${environment.apiUrl}pulse/staff`);
  }

  /** Give access. Without teams, Pulse uses the default teams for the staff member's role. */
  grant(userId: string, teams?: string[]): Observable<{ agent: PulseAgent }> {
    return this.http.post<{ agent: PulseAgent }>(
      `${environment.apiUrl}pulse/staff/${userId}/access`,
      teams ? { teams } : {},
    );
  }

  revoke(userId: string): Observable<unknown> {
    return this.http.delete(`${environment.apiUrl}pulse/staff/${userId}/access`);
  }

  /** Whether the signed-in user can open Pulse. False on any error. */
  hasAccess(): Observable<boolean> {
    return this.http.get<{ has_access: boolean }>(`${environment.apiUrl}pulse/staff/me`).pipe(
      map((res) => !!res?.has_access),
      catchError(() => of(false)),
    );
  }

  /**
   * Opens Pulse in a new tab, signed in. Call it straight from a click: the tab is opened at once
   * (so popup blockers allow it) and pointed at the one-time sign-in link when it arrives.
   */
  openPulse(next = ''): Promise<void> {
    const tab = window.open('about:blank', '_blank');
    return new Promise((resolve, reject) => {
      const params: Record<string, string> = next ? { next } : {};
      this.http.get<{ url: string }>(`${environment.apiUrl}pulse/sign-in`, { params }).subscribe({
        next: (res) => {
          if (tab) {
            tab.opener = null;
            tab.location.href = res.url;
          } else {
            window.location.href = res.url;
          }
          resolve();
        },
        error: (err) => {
          tab?.close();
          reject(err);
        },
      });
    });
  }
}
